import {
  Alert,
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Iconify from "@/components/iconify/iconify";
import {
  QA_JOB_FINISHED,
  type QaProvisionJob,
  type QaProvisionJobStatus as JobStatus,
} from "@/types/qaProvision";

const STATUS_COLOR: Record<
  JobStatus,
  "info" | "success" | "warning" | "error"
> = {
  PENDING: "info",
  RUNNING: "info",
  SUCCEEDED: "success",
  PARTIAL: "warning",
  FAILED: "error",
};

/** Event log line colour by level; anything else reads as ordinary output. */
const EVENT_COLOR: Record<string, string> = {
  error: "error.main",
  warn: "warning.main",
};

const CopyButton = ({ value, label }: { value: string; label: string }) => (
  <Tooltip title={`Copy ${label}`}>
    <IconButton
      size="small"
      onClick={() => navigator.clipboard?.writeText(value)}
    >
      <Iconify icon="mdi:content-copy" width={16} />
    </IconButton>
  </Tooltip>
);

const Mono = ({ children }: { children: React.ReactNode }) => (
  <Box component="span" sx={{ fontFamily: "monospace", fontSize: 13 }}>
    {children}
  </Box>
);

export const QaProvisionJobStatus = ({ job }: { job: QaProvisionJob }) => {
  const finished = QA_JOB_FINISHED.includes(job.status);
  const progress = job.progress;
  const users = job.users ?? [];
  const succeeded = users.filter((user) => user.status === "succeeded");
  const failed = users.filter((user) => user.status === "failed");

  // Job-wide line progress: the worker sums it across climbers, which run
  // concurrently, so this only ever moves forwards.
  const lineProgress =
    progress?.done != null && progress?.total
      ? (progress.done / progress.total) * 100
      : null;

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
        <Typography variant="h6">Job</Typography>
        <Mono>{job.jobId}</Mono>
        <Chip
          size="small"
          label={job.status}
          color={STATUS_COLOR[job.status]}
        />
        {job.mode && <Chip size="small" variant="outlined" label={job.mode} />}
        {job.productCode && (
          <Chip size="small" variant="outlined" label={job.productCode} />
        )}
      </Box>

      {!finished && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {/* Climbers run concurrently, so naming one of several would be
                misleading — the detail line below says which is being worked
                on right now. */}
            {!progress?.userCount
              ? "Starting…"
              : progress.userCount === 1
                ? "Climber 1 of 1"
                : `${progress.usersDone ?? 0} of ${progress.userCount} climbers done`}
            {progress?.phase ? ` — ${progress.phase}` : ""}
          </Typography>
          {lineProgress == null ? (
            <LinearProgress />
          ) : (
            <LinearProgress variant="determinate" value={lineProgress} />
          )}
          {progress?.detail && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: "monospace", display: "block", mt: 0.5 }}
            >
              {progress.detail}
            </Typography>
          )}
        </Box>
      )}

      {job.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {job.error}
        </Alert>
      )}

      {finished && !job.error && (
        <Alert
          severity={job.status === "SUCCEEDED" ? "success" : "warning"}
          sx={{ mb: 2 }}
        >
          Provisioned {succeeded.length} climber
          {succeeded.length === 1 ? "" : "s"}
          {failed.length ? `; ${failed.length} failed` : ""}.
        </Alert>
      )}

      {succeeded.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 2, overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Password</TableCell>
                <TableCell>Climber</TableCell>
                <TableCell align="right">Applied</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {succeeded.map((user) => (
                <TableRow key={user.index}>
                  <TableCell>
                    <Mono>{user.username}</Mono>
                    {user.username && (
                      <CopyButton value={user.username} label="username" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Mono>{user.password}</Mono>
                    {user.password && (
                      <CopyButton value={user.password} label="password" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Mono>{user.climberId}</Mono>
                  </TableCell>
                  <TableCell align="right">
                    {user.applied ?? 0}
                    {user.skipped?.length ? (
                      <Tooltip title={user.skipped.join("\n")}>
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ ml: 1 }}
                          label={`${user.skipped.length} skipped`}
                        />
                      </Tooltip>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {failed.map((user) => (
        <Alert key={user.index} severity="error" sx={{ mb: 1 }}>
          Climber {user.index + 1}: {user.error}
        </Alert>
      ))}

      {!!job.events?.length && (
        <Paper
          variant="outlined"
          sx={{ p: 2, maxHeight: 260, overflow: "auto" }}
        >
          {job.events.map((event, index) => (
            <Typography
              key={`${event.ts}-${index}`}
              variant="caption"
              sx={{
                display: "block",
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                color: EVENT_COLOR[event.level ?? ""] ?? "text.secondary",
              }}
            >
              {event.message}
            </Typography>
          ))}
        </Paper>
      )}
    </Box>
  );
};
